import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseDate } from '../api/useRequest';

export interface Store {
  write(key: string, serializedValue: string): void;
  read(key: string): string | null;
}

export interface Serializer<T> {
  serialize(value: T): string;
  deserialize(serializedValue: string): T;
}

export const combineStores = (stores: Store[], syncStores = true): Store => ({
  write(key, serializedValue) {
    stores.forEach((s) => s.write(key, serializedValue));
  },
  read(key) {
    for (const store of stores) {
      const value = store.read(key);
      if (value === null) continue;

      if (syncStores) {
        stores.filter((s) => s !== store).forEach((s) => s.write(key, value));
      }

      return value;
    }

    return null;
  },
});

export const useURLSearchParamsStore = (): Store => {
  const [searchParams, setSearchParams] = useSearchParams();

  return {
    write: useCallback(
      (key, serializedValue) => {
        const newParams = new URLSearchParams(window.location.search);
        newParams.set(key, serializedValue);
        setSearchParams(newParams, { replace: true });
      },
      [setSearchParams],
    ),
    read(key) {
      return searchParams.get(key);
    },
  };
};

export const useLocalStorageStore = (): Store =>
  useMemo(
    () => ({
      write(key, serializedValue) {
        localStorage.setItem(key, serializedValue);
      },
      read(key) {
        const value = localStorage.getItem(key);
        return value;
      },
    }),
    [],
  );

export function usePersistedState<S>(
  key: string,
  initialState: S | (() => S),
  { write, read }: Store,
  serializer: Serializer<S> = JSONSerializer,
): [S, (prevState: S) => void] {
  const persistedState = read(key);
  const [getter, setter] = useState(
    (persistedState && serializer.deserialize(persistedState)) || initialState,
  );

  const wrappedSetter: (state: S) => void = useCallback(
    (value) => {
      write(key, serializer.serialize(value));
      setter(value);
    },
    [key, serializer, write],
  );

  return [getter, wrappedSetter];
}

// Helpers

export const useURLPersistedState = <S>(
  key: string,
  initialState: S | (() => S),
  serializer = JSONSerializer,
) => usePersistedState<S>(key, initialState, useURLSearchParamsStore(), serializer);

export const useLocalPersistedState = <S>(
  key: string,
  initialState: S | (() => S),
  serializer = JSONSerializer,
) => usePersistedState<S>(key, initialState, useLocalStorageStore(), serializer);

export const JSONSerializer: Serializer<any> = {
  serialize: JSON.stringify,
  deserialize: (value) => JSON.parse(value, parseDate),
};
